import { useRef, useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { ExperienceCard } from './ExperienceCard';
import { ExperienceTitle } from './ExperienceTitle';
import { Grid } from '@/components';
import { useGetExperiences } from '../hooks';
import { ExperiencesListLoading } from './ExperiencesListLoading';
import { ExperiencesListStatus } from './ExperiencesListStatus';
import { ExperiencesListError } from './ExperiencesListError';

export const Experience = () => {
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useGetExperiences();

  const observerRef = useRef<HTMLDivElement | null>(null);
  const [showScrollDelay, setShowScrollDelay] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    setInitialLoading(true);

    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 2000);
    refetch();

    return () => {
      clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    if (initialLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (
          entry.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !showScrollDelay &&
          !initialLoading
        ) {
          setShowScrollDelay(true);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await fetchNextPage();
          setShowScrollDelay(false);
        }
      },
      { rootMargin: '300px' },
    );

    const el = observerRef.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    showScrollDelay,
    initialLoading,
  ]);

  if (isError) {
    return <ExperiencesListError refetch={refetch} />;
  }

  const experiences = data?.pages.flatMap((page) => page.data) ?? [];

  if (!isLoading && experiences.length === 0) {
    return <ExperiencesListStatus />;
  }

  return (
    <Stack px={4} gap={4}>
      <ExperienceTitle />
      <Grid gap={30} columns={4}>
        {initialLoading ? (
          <ExperiencesListLoading />
        ) : (
          experiences.map((exp) => (
            <ExperienceCard
              key={exp._id}
              id={exp._id}
              image={exp.bannerImagePath}
              label={exp.title}
              description={exp.description}
              email={exp.email}
              phone={exp.phone}
            />
          ))
        )}
        {(isFetchingNextPage || showScrollDelay) && hasNextPage && (
          <ExperiencesListLoading />
        )}
      </Grid>
      <div ref={observerRef} style={{ height: 1 }} />
    </Stack>
  );
};
